import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import env from 'utils/env';
import { formatProduct, USER_TYPE, getUserType } from 'utils/social-ecomm';
import { track, segmentEnum } from 'utils/segment';
import { selectPageTrackingFullData } from 'selectors/trackingSelector';
import {
  createTeam as createTeamAction,
  closeInviteModal as closeInviteModalAction,
} from 'services/actions/socialEcommActions';
import CloseButton from 'shared-components/CloseButton/CloseButton';
import BaseModal from 'shared-components/BaseModal/Modal';
import ShareProduct from './ShareProduct/ShareProduct';
import InviteLink from './InviteLink/InviteLink';
import EmailForm from './EmailForm/EmailForm';
import styles from './InviteModal.module.css';

const MODAL_SCREEN = {
  link: 'link',
  email: 'email',
};

const getSocialMediaCopy = (product, userType) => {
  const shareCopy = userType === USER_TYPE.member ? 'we both' : 'you';
  if (product) {
    return `Check out this ${formatProduct(
      product
    )}!\n\nJoin me in shopping items like this at up to 70% off in a FabFitFun exclusive sale, plus get a $10 credit when ${shareCopy} make a purchase.`;
  }
  return `Shop with me, earn $10 credit when ${shareCopy} make a purchase, and discover items at up to 70% off in a FabFitFun exclusive sale!`;
};

export const InviteModal = ({
  isOpen,
  product,
  experience,
  teamId,
  inviteCode,
  creatingTeam,
  planCode,
  trackingData,
  closeModal,
  createTeam,
  onInviteSent,
  onInviteError,
}) => {
  const [screen, setScreen] = useState(MODAL_SCREEN.link);
  const history = useHistory();
  const title = product
    ? 'Share this Product & Invite Your Friends to Shop'
    : 'Invite Your Friends to Shop with You';
  const inviteUrl = `${env.hostname}/redeem-invite?inviteCode=${inviteCode}`;
  const link = product ? `${inviteUrl}&sku=${product.sku}` : inviteUrl;
  const userType = getUserType(planCode);
  const socialMediaCopy = getSocialMediaCopy(product, userType);
  const facebookLoaded = !!window.FB;

  const handleCloseModal = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('inviteOpen');
    history.replace({
      search: params.toString(),
    });
    setScreen(MODAL_SCREEN.link);
    closeModal();
  };
  const handleInviteSentTracking = (ctaName) => {
    const data = {
      ...trackingData,
      invite_code: inviteCode,
      experience,
      cta_name: ctaName,
      ...(product && {
        product_id: product.productId,
        product_name: product.productName,
        retail_price: product.retailPrice,
        sale_price: product.salePrice,
      }),
    };
    track(segmentEnum.inviteSent, data);
  };
  const handleClickEmail = () => {
    handleInviteSentTracking('EMAIL');
    setScreen(MODAL_SCREEN.email);
  };
  const handleFacebookInvitation = () => {
    if (facebookLoaded) {
      handleInviteSentTracking('FB SHARE');
      window.FB.ui({
        method: 'share',
        href: inviteUrl,
        quote: socialMediaCopy,
      });
    }
  };

  useEffect(() => {
    if (isOpen && !teamId && userType === USER_TYPE.member) createTeam();
  }, [isOpen, teamId, planCode]);

  return (
    <BaseModal
      isOpen={isOpen}
      showCloseIcon
      onRequestClose={handleCloseModal}
      className={styles.modal}
    >
      <div className={styles.container}>
        <CloseButton onClick={handleCloseModal} />
        <h1 className={classNames(styles.title, { [styles.invite]: !product })}>
          {title}
        </h1>
        <p className={styles.subtitle}>
          For each invited friend*, you&apos;ll each earn a{` `}
          <strong>$10 credit</strong> to use in the sale when you both purchase
          a product.
        </p>
        {product && <ShareProduct product={product} />}
        {inviteCode && (
          <p className={styles.shareText}>
            Share your personal link to invite your friends:
          </p>
        )}
        {screen === MODAL_SCREEN.link && inviteCode && (
          <InviteLink
            link={link}
            product={product}
            socialMediaCopy={socialMediaCopy}
            onClickEmail={handleClickEmail}
            onClickShare={handleFacebookInvitation}
            onInviteSent={onInviteSent}
            facebookLoaded={facebookLoaded}
            trackInviteSent={handleInviteSentTracking}
          />
        )}
        {screen === MODAL_SCREEN.email && inviteCode && (
          <EmailForm
            inviteCode={inviteCode}
            product={product}
            creatingTeam={creatingTeam}
            onInviteSent={onInviteSent}
            onInviteError={onInviteError}
          />
        )}
        <div className={styles.notes}>
          <p>
            By sharing your personal link through any method, you agree to the
            {` `}
            <a
              href="https://legal.fabfitfun.com/#shop-with-friends-terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Shop with Friends Terms
            </a>{' '}
            and{' '}
            <a
              href="https://legal.fabfitfun.com/#terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use and Sale
            </a>
            .
          </p>
          <p>
            *Invites can only be redeemed by those who are new to FabFitFun.
          </p>
        </div>
      </div>
    </BaseModal>
  );
};

InviteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    productId: PropTypes.string,
    productName: PropTypes.string,
    retailPrice: PropTypes.string,
    salePrice: PropTypes.string,
    sku: PropTypes.string,
  }),
  experience: PropTypes.string,
  teamId: PropTypes.number,
  inviteCode: PropTypes.string,
  creatingTeam: PropTypes.bool.isRequired,
  planCode: PropTypes.string.isRequired,
  trackingData: PropTypes.shape({}).isRequired,
  closeModal: PropTypes.func.isRequired,
  createTeam: PropTypes.func.isRequired,
  onInviteSent: PropTypes.func.isRequired,
  onInviteError: PropTypes.func.isRequired,
};

InviteModal.defaultProps = {
  product: null,
  experience: null,
  teamId: null,
  inviteCode: null,
};

const mapStateToProps = (state) => ({
  isOpen: state.socialEcommState.inviteModal.isOpen,
  product: state.socialEcommState.inviteModal.product,
  experience: state.socialEcommState.inviteModal.experience,
  teamId: state.socialEcommState.team.teamId,
  inviteCode: state.socialEcommState.team.inviteCode,
  creatingTeam: state.socialEcommState.creatingTeam,
  planCode: state.userState.planCode,
  trackingData: selectPageTrackingFullData(state),
});

const mapDispatchToProps = {
  createTeam: createTeamAction,
  closeModal: closeInviteModalAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(InviteModal);
